import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useRouteError,
  useRouteLoaderData,
} from '@remix-run/react';
import { GTM_ID, generateGTMScript, generateGTMNoScript, trackPageView } from '~/utils/analytics';
import {
	json,
	type LoaderFunctionArgs,
} from '@remix-run/node'
import { AuthenticityTokenProvider } from 'remix-utils/csrf/react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import './tailwind.css';
import QueryProvider from '~/utils/query-provider';
import { csrf } from '~/.server/utils/security/csrf';
import { honeypot } from '~/.server/utils/security/honeypot';
import { getEnv } from '~/.server/utils/env'
import Header from './components/header';
import { Footer } from './components/footer';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { trpcServer } from './common/trcp';
import { Toaster, toast as showToast } from 'sonner';
import { getToast, type Toast } from './.server/utils/toast';
import { useEffect } from 'react';
import { useChangeLanguage } from "remix-i18next/react"
import { useTranslation } from "react-i18next"
import i18next from "~/i18n/server"
import { combineHeaders } from './.server/utils/misc';
import { UserContextType, UserProvider } from './utils/hooks/useUser';
import { CopyProvider } from '~/components/copy/copy-context';
import { useABTestConfig } from './utils/hooks/useABTest';
import { getAuthSession } from './.server/utils/auth';
import { ChatWidget } from './components/chat/chatWidget';
import { WhatsappChatButton } from './components/whatsappChatButton';
import { urlPattern } from '~/utils/urlPattern';
import {
  WA_CHAT_ENTRY_TEST_KEY,
  resolveServerABTestVariant,
  serializeAbBucketCookie,
  type ABTestConfig,
} from '~/utils/abTesting';

export const handle = {
  i18n: "common",
};

export async function loader({ request }: LoaderFunctionArgs) {
  const locale = await i18next.getLocale(request)
  const authSession = await getAuthSession(request);

	const [csrfToken, csrfCookieHeader] = await csrf.commitToken(request)
	const honeyProps = honeypot.getInputProps()
  const { toast, headers: toastHeaders } = await getToast(request)

	// const userId = await getUserId(request)
  const queryClient = new QueryClient();

  await queryClient.prefetchQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      return await trpcServer(request).workspace.getAllWorkspaceCategories.query();
    },
  });

  await queryClient.prefetchQuery({
    queryKey: ['amenities'],
    queryFn: async () => await trpcServer(request).workspace.getAllWorkspaceAmenities.query()
  });

  await queryClient.prefetchQuery({
    queryKey: ['rentalTypes'],
    queryFn: async () => await trpcServer(request).workspace.getAllWorkspaceRentalTypes.query()
  });

  // fetchQuery (not prefetchQuery): same cache entry the client hydrates from, but the
  // loader also needs the VALUE — the waChatEntry test below is assigned server-side so the
  // first paint already shows the right chat entry (a localStorage test would flip after
  // hydration, visibly, for a floating button).
  const abTests: ABTestConfig[] = await queryClient.fetchQuery({
    queryKey: ['ab-tests'],
    queryFn: async () => await trpcServer(request).abTest.getActiveTests.query()
  });

  let copyOverrides: { key: string; locale: string; value: string }[] = [];
  try {
    copyOverrides = await trpcServer(request).copy.getOverrides.query();
  } catch (e) {
    console.error('[copy] failed to load overrides; falling back to registry defaults', e);
  }

  // WhatsApp-agent rollout (option 1: one chat door per visitor): a server-assigned A/B
  // test driven by the `ab_test` row `waChatEntry` — weights are the split (99/1 = 1%),
  // `is_active=false` or `variant_weight=0` turns it off, no deploy. `disabled` guards the
  // case where the variant cannot work at all: without WHATSAPP_PUBLIC_NUMBER the button
  // renders null and a variant visitor would have NO chat entry.
  const {
    bucket: waBucket,
    assigned: waBucketAssigned,
    variant: waChat,
  } = resolveServerABTestVariant({
    testKey: WA_CHAT_ENTRY_TEST_KEY,
    cookieHeader: request.headers.get('cookie'),
    tests: abTests,
    disabled: !process.env.WHATSAPP_PUBLIC_NUMBER?.trim(),
  });

  const initialUserData: UserContextType = {
    userId: authSession.userId, // Since we don't need the full user profile
    isSubscribed: authSession.isSubscribed,
    subscriptionExpiresAt: authSession.subscriptionExpiresAt,
    isLoading: false,
    isAdmin: authSession.isAdmin,
  };

	return json(
		{
			ENV: getEnv(),
			csrfToken,
			honeyProps,
      toast,
      initialUserData,
      locale,
      copyOverrides,
      waChat,
      dehydratedState: dehydrate(queryClient),
    },
    {
      headers: combineHeaders(
				csrfCookieHeader ? { 'set-cookie': csrfCookieHeader } : null,
				toastHeaders,
        waBucketAssigned ? { 'set-cookie': serializeAbBucketCookie(WA_CHAT_ENTRY_TEST_KEY, waBucket) } : null,
        { "Content-Language": locale }
			),
		}
  )
}

function App() {
  const data = useLoaderData<typeof loader>();
  const location = useLocation();
  useABTestConfig();
  
  // Track page views when location changes
  useEffect(() => {
    // Only run on client
    if (typeof window !== 'undefined') {
      trackPageView(location.pathname, document.title, { wa_chat_variant: data.waChat });
    }
  }, [location, data.waChat]);
  
  // Admin is an app shell (shadcn sidebar) — drop the marketing footer and the
  // outer page padding there so the sidebar can be full-bleed and isn't overlapped.
  const isAdmin = location.pathname.startsWith('/admin');
  // Workspace detail pages (/espacios/:city/:category/:space) mount their own
  // contextual <WhatsappChatButton workspace={...}> next to the workspace data,
  // so the generic site-wide one here is skipped there to avoid a duplicate
  // floating button.
  const isWorkspaceDetail = Boolean(urlPattern.parse(location.pathname).space);

  return (
    <>
      <Header />
      <main
        className={
          isAdmin
            ? 'flex min-h-[calc(100vh_-_theme(spacing.16))] flex-1 flex-col bg-muted/40'
            : 'flex min-h-[calc(100vh_-_theme(spacing.16))] flex-1 flex-col gap-4 bg-muted/40 p-4 md:gap-8 md:p-10'
        }
      >
        <Outlet />
      </main>
      {isAdmin ? null : <Footer />}
      {data.toast ? <ShowToast toast={data.toast} /> : null}
      {/* One chat door per visitor (A/B `waChatEntry`, option 1): 'control' keeps today's web
          ChatWidget; 'variant' gets the WhatsApp button in the SAME spot (bottom-right) instead — never
          both. Workspace detail pages mount their own contextual WhatsappChatButton (with
          the space prefill), so the generic one is skipped there for the test group and the
          control group simply keeps the widget. */}
      {!isAdmin && !location.pathname.startsWith('/cuenta') && data.waChat !== 'variant' ? (
        <ChatWidget locale={data?.locale ?? 'es'} />
      ) : null}
      {!isAdmin && !location.pathname.startsWith('/cuenta') && !isWorkspaceDetail && data.waChat === 'variant' ? (
        <WhatsappChatButton />
      ) : null}
    </>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  // Layout renders for BOTH the normal tree and the root ErrorBoundary path
  // (e.g. after a hydration mismatch throws). useLoaderData() throws in the
  // latter case ("You cannot `useLoaderData` in an errorElement"), which was
  // turning a recoverable render error into a hard crash. useRouteLoaderData
  // returns undefined instead, so the fallbacks below still apply.
  const data = useRouteLoaderData<typeof loader>('root');
  const { i18n } = useTranslation() || {};
  useChangeLanguage(data?.locale || 'es');

  return (
    <html lang={i18n?.language || 'es'} dir={i18n?.dir?.() || 'ltr'}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {/* Google Tag Manager */}
        <script
          dangerouslySetInnerHTML={{
            __html: generateGTMScript(GTM_ID)
          }}
        />
        {/* Umami Analytics - cookieless, no consent needed */}
        <script
          defer
          src="https://umami.compartirespacios.com/script.js"
          data-website-id="bc5987c2-bcf0-4317-bf95-a0f083ee8805"
        />
      </head>
      <body>
        {/* Google Tag Manager (noscript) */}
        <noscript dangerouslySetInnerHTML={{ __html: generateGTMNoScript(GTM_ID) }} />
        {children}
        <Toaster closeButton position="top-center" />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

// Separate error boundary component 
function RootErrorBoundary() {
  return (
    <div className="flex h-full flex-col items-center justify-center">
      <h1>Oops! Algo ha pasado</h1>
    </div>
  );
}

// Add error boundary
// NOTE: this must NOT render its own <html>/<head>/<body> — `Layout` above
// already wraps whatever this returns in the document shell (that's the
// whole point of the Layout export). Returning a full document here nested
// a second <html> inside Layout's <body>, tripping React's DOM nesting
// validation and breaking the error page.
export function ErrorBoundary() {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return <RootErrorBoundary />;
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<AuthenticityTokenProvider token={data.csrfToken}>
        <QueryProvider>
          <UserProvider initialData={data.initialUserData}>
            <CopyProvider>
              <App />
            </CopyProvider>
          </UserProvider>
        </QueryProvider>
			</AuthenticityTokenProvider>
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders);

function ShowToast({ toast }: { toast: Toast }) {
	const { id, type, title, description, actionLabel, actionUrl } = toast
	useEffect(() => {
		setTimeout(() => {
			const toastOptions: Parameters<typeof showToast.success>[1] = {
				id,
				description,
			}

			if (actionLabel && actionUrl) {
				toastOptions.action = {
					label: actionLabel,
					onClick: () => { window.location.href = actionUrl },
				}
			}

			showToast[type](title, toastOptions)
		}, 0)
	}, [description, id, title, type, actionLabel, actionUrl])
	return null
}
